import { usePublishQSetsMutation } from '@exam-management/graphql';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import { QSetPublishConfirmationModal, QSetPublishIncompleteModal } from '../../components';
import type { PublicationResult } from '../../types';

enum PublishQSetAction {
  CONFIRM_PUBLISH = 'CONFIRM_PUBLISH',
  INCOMPLETE_PUBLISH = 'INCOMPLETE_PUBLISH',
}

interface QSetPublicationModalProps {
  open: boolean;
  onClose: () => void;
  qSetIds: string[];
  onCompleted: () => void;
}

const DEFAULT_PUBLICATION_RESULT = {
  successes: [],
  errors: [],
};

export default function QSetPublicationModal(props: Readonly<QSetPublicationModalProps>) {
  const { open: isOpen, onClose, qSetIds, onCompleted } = props;
  const [publishQSets, { loading: isPublishingQSets }] = usePublishQSetsMutation();
  const { showSnackbar } = useSnackbar();
  const [publicationResult, setPublicationResult] = useState<PublicationResult>(
    DEFAULT_PUBLICATION_RESULT,
  );
  const [action, setAction] = useState<PublishQSetAction>(PublishQSetAction.CONFIRM_PUBLISH);

  const handleConfirmPublish = async () => {
    await publishQSets({
      variables: {
        input: {
          qSetIds,
        },
      },
      onCompleted: () => {
        onClose();
        if (qSetIds.length === 1) {
          showSnackbar({
            variant: 'success',
            message: 'QSet has been published',
          });
        } else {
          showSnackbar({
            variant: 'success',
            message: 'QSets have been published',
          });
        }
        onCompleted();
      },
      onError: (error) => {
        const extensions = error.cause?.extensions;
        if (!extensions || typeof extensions !== 'object' || Array.isArray(extensions)) {
          return [];
        }
        setPublicationResult((extensions.details as Record<string, PublicationResult>).value);
        setAction(PublishQSetAction.INCOMPLETE_PUBLISH);
      },
    });
  };

  const handlePublishCompleteQSets = async () => {
    await publishQSets({
      variables: {
        input: {
          qSetIds: publicationResult.successes.map((success) => success.qSetId),
        },
      },
      onCompleted: () => {
        onClose();
        if (publicationResult.successes.length === 1) {
          showSnackbar({
            variant: 'success',
            message: 'QSet has been published',
          });
        } else {
          showSnackbar({
            variant: 'success',
            message: 'QSets have been published',
          });
        }
        onCompleted();
      },
      onError: (error) => {
        const extensions = error.cause?.extensions;
        if (!extensions || typeof extensions !== 'object' || Array.isArray(extensions)) {
          return [];
        }
        setPublicationResult((extensions.details as Record<string, PublicationResult>).value);
        setAction(PublishQSetAction.INCOMPLETE_PUBLISH);
      },
    });
  };

  const resetPublicationResult = () => {
    setPublicationResult(DEFAULT_PUBLICATION_RESULT);
  };

  const handleCancel = () => {
    onClose();
    setAction(PublishQSetAction.CONFIRM_PUBLISH);
    resetPublicationResult();
  };

  return (
    <>
      <QSetPublishConfirmationModal
        open={isOpen && action === PublishQSetAction.CONFIRM_PUBLISH}
        onAccept={handleConfirmPublish}
        onClose={handleCancel}
      />
      <QSetPublishIncompleteModal
        open={isOpen && action === PublishQSetAction.INCOMPLETE_PUBLISH}
        onAccept={handlePublishCompleteQSets}
        onClose={handleCancel}
        onDiscard={handleCancel}
        errors={publicationResult.errors}
        successes={publicationResult.successes}
        loading={isPublishingQSets}
      />
    </>
  );
}
